/*
 * Lure Effectiveness leaderboard.
 *
 * Sits on SimsPage as a widget. Ranks lure tags by click rate across the
 * supplied SEND_LOG slice so scope filters narrow the result. Display-only:
 * rows deliberately don't drill (a tag scope only applied to some widgets,
 * which confused more than it helped). Mirrors BarSegmentCard visuals
 * (24px shimmer bars, primary fill, value to the right).
 *
 * Postgres mapping: see tagAnalytics.js header.
 * Integration hook: `tagApi.useTagBreakdownQuery({ scope, period })`.
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useThemeContext } from 'themes/ThemeContext';
import { motion } from 'motion/react';
import MainCard from '../../../components/cards/MainCard';
import CountUp from 'react-countup';
import { clickRateByTag, TAG_DESCRIPTIONS } from '../tagAnalytics';

const fadeUp = { hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };

function rateColor(rate) {
    if (rate >= 30) return 'var(--red)';
    if (rate >= 15) return 'var(--amber)';
    return 'var(--green)';
}

function ShimmerBar({ color, opacity = 1, pct, delay, isDark }) {
    const [value, setValue] = useState(0);

    useEffect(() => {
        setValue(0);
        const t = setTimeout(() => setValue(pct), 150 + delay * 1000);
        return () => clearTimeout(t);
    }, [pct, delay]);

    return (
        <LinearProgress
            variant="determinate"
            value={value}
            sx={{
                height: 24,
                borderRadius: 1,
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'grey.100',
                '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                    background: color,
                    opacity,
                    transition: 'transform 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
                    transitionDelay: `${delay}s`,
                },
            }}
        />
    );
}

export default function LureEffectivenessCard({ events, tagRows }) {
    const theme = useTheme();
    const { themeMode } = useThemeContext();
    const isDark = themeMode === 'dark';

    const rows = (Array.isArray(tagRows) ? tagRows : clickRateByTag(events))
        .filter((r) => r.sends >= 5)
        .sort((a, b) => b.rate - a.rate)
        .slice(0, 5);

    if (rows.length === 0) return null;
    const totalSends = rows.reduce((a, r) => a + r.sends, 0);

    return (
        <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                                Lure effectiveness
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                Tags ranked by click rate for the current scope. Tags with fewer than 5 sends are hidden.
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 2 }}>
                            <Typography sx={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.disabled' }}>
                                Sample
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
                                {totalSends.toLocaleString()} sends
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {rows.map((r, i) => {
                            // Bar fill equals the actual click rate (rate is already a
                            // 0-100% value). Normalising to the max made the top row fill
                            // the track 100% while its label read 32%, which misled.
                            const widthPct = r.rate;
                            const color = rateColor(r.rate);
                            return (
                                <Tooltip key={r.tag} title={TAG_DESCRIPTIONS[r.tag] || r.label} placement="left" arrow>
                                    <Box sx={{ cursor: 'default' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', minWidth: 14 }}>
                                                    {i + 1}
                                                </Typography>
                                                <Typography className="lure-label" sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary', transition: 'color 150ms' }}>
                                                    {r.label}
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                                                    {r.clicks.toLocaleString()} of {r.sends.toLocaleString()} clicked
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color, letterSpacing: '-0.01em' }}>
                                                <CountUp end={r.rate} duration={1.2} decimals={r.rate < 10 ? 1 : 0} />%
                                            </Typography>
                                        </Box>
                                        <ShimmerBar
                                            color={color}
                                            opacity={1 - (i / rows.length) * 0.3}
                                            pct={widthPct}
                                            delay={i * 0.08}
                                            isDark={isDark}
                                        />
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Box>
                </Box>
            </MainCard>
        </motion.div>
    );
}
